import { Palette } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ShortcutsDialog } from '../../features/viewer-controls/ShortcutsDialog';
import { useColorStore } from '../../stores/colorStore';

/** Shorten common ICC profile descriptions for display (full name stays in the
 *  tooltip). e.g. "sRGB built-in" / "sRGB IEC61966-2.1" → "sRGB". */
function prettyProfileName(name?: string): string {
  if (!name) return 'ICC';
  if (/s[\s-]?rgb/i.test(name)) return 'sRGB';
  return name.replace(/\s*built[\s-]?in\s*$/i, '').trim() || name;
}

export function StatusBar() {
  const colorAvailable = useColorStore((s) => s.available);
  const colorEnabled = useColorStore((s) => s.enabled);
  const colorName = useColorStore((s) => s.name);
  const colorLoading = useColorStore((s) => s.nameLoading);
  const colorToggle = useColorStore((s) => s.toggle);
  const colorUnavailable = useColorStore((s) => s.unavailable);

  return (
    <div className="flex h-7 shrink-0 items-center justify-between border-t px-3 text-xs text-muted-foreground">
      <span>DICOM Viewer</span>
      <div className="flex items-center gap-3">
        {colorAvailable && (
          <button
            type="button"
            onClick={colorToggle}
            className="flex items-center gap-1.5 rounded-md px-1.5 py-0.5 transition-colors hover:bg-accent hover:text-foreground"
            title={`ICC color correction: ${colorEnabled ? 'on' : 'off'} — ${colorName ?? 'profile'}`}
          >
            <Palette className={cn('h-3.5 w-3.5', colorEnabled ? 'text-primary' : 'opacity-60')} />
            {colorEnabled && colorLoading ? (
              <Skeleton className="h-3 w-10" />
            ) : (
              <span className={cn(!colorEnabled && 'opacity-60')}>
                {colorEnabled ? prettyProfileName(colorName) : 'Raw Color'}
              </span>
            )}
          </button>
        )}
        {!colorAvailable && colorUnavailable && (
          <span
            className="flex items-center gap-1.5 px-1.5 py-0.5 opacity-60"
            title={`This slide carries a color profile${colorName ? ` (${colorName})` : ''} that could not be applied, so it is shown in its raw color.`}
          >
            <Palette className="h-3.5 w-3.5" />
            Raw Color
          </span>
        )}
        <ShortcutsDialog>
          <button
            type="button"
            className="rounded-md px-1.5 py-0.5 transition-colors hover:bg-accent hover:text-foreground"
          >
            Shortcuts
          </button>
        </ShortcutsDialog>
      </div>
    </div>
  );
}
